import React, { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, type SessionRow } from '../lib/api.js';
import { UploadList } from './UploadList.js';
import { FhirImportPanel } from './FhirImportPanel.js';
import { FileRow } from './portal/FileRow.js';
import { StatsBar } from './portal/StatsBar.js';
import type { UploadEntry, UploadStatus } from './portal/uploadTypes.js';
import { computeSpeed, type SpeedSample } from './portal/uploadProgress.js';

const MAX_CONCURRENT = 3;
const ACCEPT = '.csv,.tsv,.xlsx,.xls,.json,.zip';

function uid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Flat-file / bulk-dump session body for /sessions/:id: an extravagant upload
 * drop-pad — per-file progress bars, throughput, ETA, and an aggregate stats bar
 * (the same rich treatment as the client portal, reusing StatsBar + FileRow) —
 * plus the FHIR import launcher.
 *
 * Flat-file imports flow through the FHIR hub: upload → FhirImportPanel
 * (FhirMappingSpec) → fhir_resource_cache → CerboFhirExporter.
 */
export function FlatFileSessionPanel({ session, reveal = false }: { session: SessionRow; reveal?: boolean }) {
  const sessionId = session.id;
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: uploadsData } = useQuery({
    queryKey: ['uploads', sessionId],
    queryFn: () => api.listUploads(sessionId),
    refetchInterval: 3000,
  });
  const uploads = uploadsData?.uploads ?? [];

  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const activeUploadId = selectedUploadId ?? uploads[0]?.id ?? null;

  const [dragging, setDragging] = useState(false);
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const speedSamples = useRef<Map<string, SpeedSample[]>>(new Map());
  const filesById = useRef<Map<string, File>>(new Map());

  const updateEntry = useCallback((id: string, patch: Partial<UploadEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const runUpload = useCallback(
    async (id: string) => {
      const file = filesById.current.get(id);
      if (!file) return;
      speedSamples.current.set(id, [{ ts: Date.now(), bytes: 0 }]);
      updateEntry(id, { status: 'uploading', bytesUploaded: 0, speedBps: 0, etaSec: 0, errorMessage: undefined });
      try {
        await api.uploadFileToSessionXHR(sessionId, file, {
          onProgress: (loaded) => {
            const now = Date.now();
            const samples = (speedSamples.current.get(id) ?? []).filter((s) => now - s.ts <= 2000);
            samples.push({ ts: now, bytes: loaded });
            speedSamples.current.set(id, samples);
            const speed = computeSpeed(samples, loaded, now);
            const remaining = Math.max(0, file.size - loaded);
            updateEntry(id, { bytesUploaded: loaded, speedBps: speed, etaSec: speed > 0 ? remaining / speed : 0 });
          },
        });
        speedSamples.current.delete(id);
        updateEntry(id, { status: 'complete', bytesUploaded: file.size, speedBps: 0, etaSec: 0 });
        void qc.invalidateQueries({ queryKey: ['uploads', sessionId] });
      } catch (e) {
        speedSamples.current.delete(id);
        const msg = e instanceof Error ? e.message : String(e);
        updateEntry(id, { status: 'failed', speedBps: 0, etaSec: 0, errorMessage: msg });
        toast.error(`Upload failed (${file.name}): ${msg}`);
      }
    },
    [sessionId, qc, updateEntry],
  );

  const runQueue = useCallback(
    async (ids: string[]) => {
      const queue = [...ids];
      const worker = async () => {
        for (;;) {
          const id = queue.shift();
          if (!id) return;
          await runUpload(id);
        }
      };
      await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, queue.length || 1) }, () => worker()));
    },
    [runUpload],
  );

  const ingestFiles = useCallback(
    (files: FileList | File[] | null) => {
      const list = files ? Array.from(files) : [];
      if (list.length === 0) return;
      const fresh: UploadEntry[] = list.map((file) => {
        const id = uid();
        filesById.current.set(id, file);
        return { id, file, status: 'queued' as UploadStatus, bytesUploaded: 0, speedBps: 0, etaSec: 0 };
      });
      setEntries((prev) => [...fresh, ...prev]);
      void runQueue(fresh.map((e) => e.id));
    },
    [runQueue],
  );

  const retry = useCallback(
    (id: string) => {
      updateEntry(id, { status: 'queued', bytesUploaded: 0, speedBps: 0, etaSec: 0, errorMessage: undefined });
      void runQueue([id]);
    },
    [updateEntry, runQueue],
  );

  const clearFinished = useCallback(() => {
    setEntries((prev) => {
      const keep = prev.filter((e) => e.status === 'uploading' || e.status === 'queued');
      const keepIds = new Set(keep.map((e) => e.id));
      for (const id of [...filesById.current.keys()]) if (!keepIds.has(id)) filesById.current.delete(id);
      return keep;
    });
  }, []);

  const anyActive = entries.some((e) => e.status === 'uploading' || e.status === 'queued');
  const finishedCount = entries.filter((e) => e.status === 'complete' || e.status === 'failed').length;

  return (
    <div className="space-y-5">
      {/* ── Files ── */}
      <Card title="Files" subtitle={`${uploads.length} uploaded`}>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            ingestFiles(e.dataTransfer.files);
          }}
          className="rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors"
          style={{
            borderColor: dragging ? 'var(--accent-blue)' : 'var(--border)',
            background: dragging ? 'rgba(56,189,248,0.08)' : 'var(--bg-primary)',
          }}
        >
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {dragging ? 'Drop to upload' : 'Drag files here, or'}
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
          >
            Choose files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            onChange={(e) => {
              ingestFiles(e.target.files);
              e.target.value = '';
            }}
            className="hidden"
          />
          <div className="mt-2 text-[10px]" style={{ color: 'var(--text-dimmed)' }}>
            CSV / TSV / XLSX / JSON / ZIP. Per-file progress, throughput & ETA below.
          </div>
        </div>

        {/* ── Extravagant progress: aggregate stats + per-file rows ── */}
        {entries.length > 0 && (
          <div className="mt-3 space-y-2">
            <StatsBar entries={entries} />
            <div className="space-y-2">
              {entries.map((e) => (
                <FileRow key={e.id} entry={e} onRetry={retry} />
              ))}
            </div>
            {!anyActive && finishedCount > 0 && (
              <button
                type="button"
                onClick={clearFinished}
                className="text-[11px] font-semibold transition-opacity hover:opacity-80"
                style={{ color: 'var(--text-dimmed)' }}
              >
                Clear finished
              </button>
            )}
          </div>
        )}

        {uploads.length > 1 && (
          <label className="mt-3 block">
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--text-secondary)' }}>
              Active file (preview)
            </div>
            <div className="text-[11px] mb-1" style={{ color: 'var(--text-dimmed)' }}>
              Drives the Data / Cache / Cerbo-draft previews and pre-fills the pairing below. What actually imports is the file→spec pairing under <span style={{ color: 'var(--text-secondary)' }}>FHIR import</span>.
            </div>
            <select
              value={activeUploadId ?? ''}
              onChange={(e) => setSelectedUploadId(e.target.value || null)}
              className="w-full rounded-lg px-3 py-1.5 text-sm outline-none"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            >
              {uploads.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.filename}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="mt-3">
          <UploadList sessionId={sessionId} />
        </div>
      </Card>

      {/* ── FHIR import — the one write path (→ fhir_resource_cache → Cerbo) ── */}
      <FhirImportPanel sessionId={sessionId} uploads={uploads} activeUploadId={activeUploadId} reveal={reveal} />
    </div>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{title}</div>
        {subtitle && <div className="text-[11px] text-text-dimmed">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
